import { useRef, useState, type Dispatch, type ReactNode } from 'react';
import { Pin } from 'lucide-react';
import {
  ResultsFilterChips,
  ResultsSortSheet,
  type ResultsFilterChip,
} from '../../shared/results_page/results_filter_chips.js';
import { ResultsSort } from '../../shared/results_page/results_sort.js';
import { type ResultsLayoutProps } from '../../shared/results_page/results_layout.js';
import type { FlightExplorerOffer } from './search_source.js';
import { useExplorerEnv } from './explorer_env.js';
import type { FlightFilterState, SortKey } from '../results/filter_logic.js';
import type { ExplorerEvent, ExplorerState } from './explorer_state.js';

// Os controles de corte e ordem da página do explorer: na linha da meta no
// largo (`MetaActions`) e na fila de chips da barra no estreito
// (`ExplorerFilterChips`, #4003).

/** As ordens do explorer, na ordem do menu (a linha da meta e a folha dos chips). */
const EXPLORER_SORTS: readonly SortKey[] = ['rec', 'price', 'dur', 'dep', 'arr'];

/** O "fixados" da linha da meta (o corte pelas ofertas fixadas). */
export type ExplorerPinnedFilter = { count: number; active: boolean; onToggle: () => void };

type OpenSheet = 'filters' | 'sort' | null;

/** A linha da meta no largo: o "fixados" e o ordenador. */
export function MetaActions({
  state,
  offers,
  frozen,
  pinnedFilter,
  send,
}: Readonly<{
  state: ExplorerState;
  offers: FlightExplorerOffer[];
  frozen: boolean;
  pinnedFilter: ExplorerPinnedFilter | undefined;
  send: Dispatch<ExplorerEvent>;
}>): ReactNode {
  const { copy } = useExplorerEnv();
  return (
    <>
      {pinnedFilter && (
        <button
          type="button"
          className={`fex-pinned-filter${pinnedFilter.active ? ' is-active' : ''}`}
          aria-pressed={pinnedFilter.active}
          onClick={pinnedFilter.onToggle}
        >
          <Pin size={13} fill={pinnedFilter.active ? 'currentColor' : 'none'} aria-hidden />
          {copy.page.pinnedFilter(pinnedFilter.count)}
        </button>
      )}
      {offers.length > 1 && (
        <ResultsSort
          value={state.filter.sort}
          orders={EXPLORER_SORTS}
          sortLabel={copy.page.sortLabel}
          orderLabel={(sort) => copy.page.sortMenu[sort]}
          disabled={frozen}
          onChange={(sort) => {
            send({ t: 'sort_changed', sort });
          }}
        />
      )}
    </>
  );
}

/**
 * A fila de chips do VOO no palco estreito — a mesma anatomia da do hotel
 * (#3140): `Filtros` com o contador (a folha de cortes que a linha da meta
 * abriria), a ordenação (a folha de ordenar) e o liga/desliga da política,
 * que vira na hora. O "fixados" da linha da meta vem junto como liga/desliga:
 * com a fila na barra, a linha da meta fica só com a contagem.
 */
export function ExplorerFilterChips({
  filter,
  offers,
  activeCount,
  frozen,
  pinned,
  filtersSheet,
  onFilterChange,
  onSortChange,
}: Readonly<{
  filter: FlightFilterState;
  offers: readonly FlightExplorerOffer[];
  /** Os eixos em pé — o contador do `Filtros`, o mesmo do botão da linha. */
  activeCount: number;
  frozen: boolean;
  pinned: ExplorerPinnedFilter | undefined;
  /** A folha de cortes da página (`filtersOf`), aberta pelo chip `Filtros`. */
  filtersSheet: NonNullable<ResultsLayoutProps['filtersSheet']>;
  onFilterChange: (filter: FlightFilterState) => void;
  onSortChange: (sort: SortKey) => void;
}>): ReactNode {
  const { copy } = useExplorerEnv();
  const [sheet, setSheet] = useState<OpenSheet>(null);
  const chipRefs = useRef(new Map<string, HTMLButtonElement>());
  /** O chip que abriu a folha em cena — o alvo do foco de volta. */
  const openerRef = useRef<HTMLButtonElement | null>(null);
  const openSheet = (next: Exclude<OpenSheet, null>) => () => {
    openerRef.current = chipRefs.current.get(next) ?? null;
    setSheet(next);
  };
  const onOpenChange = (open: boolean) => {
    if (!open) setSheet(null);
  };
  const chips: ResultsFilterChip[] = [
    {
      id: 'filters',
      kind: 'filters',
      label: copy.filter.filters,
      active: activeCount > 0,
      badge: activeCount,
      onPress: openSheet('filters'),
    },
    ...(offers.length > 1
      ? [
          {
            id: 'sort',
            kind: 'sort' as const,
            label: copy.page.sortMenu[filter.sort],
            active: filter.sort !== 'rec',
            onPress: openSheet('sort'),
          },
        ]
      : []),
    // Sem veredito de política em nenhuma oferta, o corte zeraria a lista. Ligado,
    // ele fica: a página que o corte devolveu pode vir vazia, e o chip é a volta.
    ...(filter.withinPolicyOnly || offers.some((offer) => offer.policy)
      ? [
          {
            id: 'policy',
            kind: 'toggle' as const,
            label: copy.page.chips.policy,
            active: filter.withinPolicyOnly,
            onPress: () => {
              onFilterChange({ ...filter, withinPolicyOnly: !filter.withinPolicyOnly });
            },
          },
        ]
      : []),
    ...(pinned
      ? [
          {
            id: 'pinned',
            kind: 'toggle' as const,
            label: copy.page.pinnedFilter(pinned.count),
            active: pinned.active,
            onPress: pinned.onToggle,
          },
        ]
      : []),
  ];
  return (
    <>
      <ResultsFilterChips
        chips={chips}
        label={copy.page.chips.toolbar}
        disabled={frozen}
        chipRefs={chipRefs}
      />
      {filtersSheet({ open: sheet === 'filters', onOpenChange, returnFocusTo: openerRef })}
      <ResultsSortSheet
        open={sheet === 'sort'}
        onOpenChange={onOpenChange}
        returnFocusTo={openerRef}
        title={copy.page.sortLabel}
        backLabel={copy.page.chips.back}
        orders={EXPLORER_SORTS}
        value={filter.sort}
        orderLabel={(sort) => copy.page.sortMenu[sort]}
        locked={frozen}
        onChange={onSortChange}
      />
    </>
  );
}
